import { yupResolver } from '@hookform/resolvers/yup';
import { forEach, isNil, map, omitBy } from 'lodash';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { Block } from '@/components/block';
import { Button } from '@/components/button';
import { ButtonGroup } from '@/components/button-group';
import { Heading } from '@/components/heading';
import { Label, Radio, RadioGroup, TextInput } from '@/components/inputs';
import { InputGroup } from '@/components/inputs/input-group';
import { NumberSpinner } from '@/components/inputs/number-spinner';
import { RadioButtonInner } from '@/components/inputs/radio/radio-button-inner';
import { RadioTabs } from '@/components/inputs/radio-tabs';
import { Link } from '@/components/link';
import { LocationAutocomplete } from '@/components/location-autocomplete';
import { Text } from '@/components/text';
import { Select } from '@/components/ui/inputs/select';
import { UserMessage } from '@/components/ui/user-message';
import { useReCaptcha } from '@/hooks/use-recaptcha';
import { fluentProvider } from '@/lib/api/providers/fluent';
import {
  UNIT_VOLUME_CBM_FULL,
  UNIT_VOLUME_CFT_FULL,
  UNIT_WEIGHT_KG,
  UNIT_WEIGHT_LB,
  USER_MESSAGES,
} from '@/lib/constants';
import { marketingPages } from '@/lib/constants/marketing-site';
import { QuoteFormInputsInterface } from '@/types';
import { PlaceInterface } from '@/types/api-types';
import { getTradeRegionForCountry } from '@/utils/helpers/get-trade-region-for-country';
import { logEvent } from '@/utils/logger';
import { countriesSelectBoxOptions } from '@/utils/places';
import { cn, tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import {
  CARGO_TYPES,
  cargoTypeSchemas,
  CONTAINER_ITEM_CATEGORY,
  QUOTE_FORM_FIELDS,
  QUOTE_FORM_FIELDS_CARGO_DETAILS,
  SPECIAL_HANDLING,
  validationSchema,
} from './constants';
import { getQuoteFormDefaultValues } from './utils';

const CONTAINER_ITEM_OPTIONS = map(CONTAINER_ITEM_CATEGORY, (itemCategory) => {
  return { value: itemCategory, label: itemCategory };
});

const QuoteForm = () => {
  const { data: session } = useSession();
  const [originCountryCode, setOriginCountryCode] = useState('');
  const [destinationCountryCode, setDestinationCountryCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { recaptchaRef, executeReCaptcha, onReCaptchaChange } = useReCaptcha();

  const router = useRouter();

  const handleQuoteRequest: SubmitHandler<QuoteFormInputsInterface> = async (data) => {
    try {
      // Execute the reCAPTCHA when the form is submitted
      const recaptchaToken = await executeReCaptcha();

      // Remove all the empty, undefined or null fields from the data object
      const populatedData = omitBy(data, (value) => {
        return isNil(value) || value === '' || value === 0;
      });

      const originRegion = getTradeRegionForCountry(originCountryCode);
      const destinationRegion = getTradeRegionForCountry(destinationCountryCode);
      const tradeLaneName =
        originRegion === destinationRegion ? `Intra ${originRegion}` : `${originRegion} to ${destinationRegion}`;

      // Analytics Event
      trackUser.event('Submit Quote Form', {
        cargoType: data.cargoType,
        country: data.country,
        sendsCargo: data.sendsCargoRegularly,
        readyForPickup: data.readyForPickup,
        specialHandling: data.specialHandling,
        originCountryCode,
        destinationCountryCode,
        originRegion,
        destinationRegion,
        tradeLane: tradeLaneName,
        userAccountType: session?.user?.usagePurpose || '',
        userIsFreightForwarder: session?.user.isFreightForwarder || '',
      });

      trackUser.conversion({ category: 'QuoteForm', action: 'SubmittedQuote' });

      try {
        const newQuote = await fluentProvider.addQuote({
          ...(populatedData as QuoteFormInputsInterface),
          recaptchaToken,
        });

        const populatedDataWithQuoteId = {
          ...populatedData,
          // We need the newly created quote id to add to the email
          id: newQuote.id,
        };

        await fluentProvider.sendQuoteEmails(populatedDataWithQuoteId as QuoteFormInputsInterface);

        // Reset the error message just in case one is
        // showing after an unsuccessful submission
        setErrorMessage(null);
      } catch (error: any) {
        setErrorMessage(`${error.message}. ${USER_MESSAGES.TRY_AGAIN}`);
        logEvent.error('Quote Form Error', { message: error.message });

        // Reset reCAPTCHA on error
        recaptchaRef.current?.reset();

        // Move the screen to the top so that the user can see the error message
        window.scrollTo(0, 0);
      }
    } catch (error: any) {
      setErrorMessage(`${error.message}. ${USER_MESSAGES.TRY_AGAIN}`);
      logEvent.error('Quote Form Error', { message: error.message });

      // Reset reCAPTCHA on error
      recaptchaRef.current?.reset();

      // Move the screen to the top so that the user can see the error message
      window.scrollTo(0, 0);
    }
  };

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    watch,
    trigger,
  } = useForm<QuoteFormInputsInterface>({
    defaultValues: getQuoteFormDefaultValues(session),
    resolver: yupResolver(validationSchema),
  });

  const cargoTypeFieldValue = watch(QUOTE_FORM_FIELDS.CARGO_TYPE);

  /**
   * Handle the change event for the cargo type field
   * @param val The value of the cargo type field
   *
   * We want to account for edge cases where the user may have entered
   * data in the form and then changed the cargo type. In this case,
   * we want to reset the values of the fields that are not
   * applicable to the new cargo type.
   */
  const handleCargoTypeChange = (val: string) => {
    // Get the schema for the selected cargo type
    const schema = cargoTypeSchemas[val];

    // Loop through all the cargo details fields
    forEach(QUOTE_FORM_FIELDS_CARGO_DETAILS, (fieldName) => {
      // Check if the field should be displayed based on the schema
      const shouldDisplay = schema[fieldName];

      // If the field should not be displayed, reset its value to the default value
      if (!shouldDisplay) {
        setValue(fieldName, getQuoteFormDefaultValues(session)[fieldName]);
      }
    });

    // Set the value of the cargo type field
    setValue(QUOTE_FORM_FIELDS.CARGO_TYPE, val);

    // Trigger validation for the cargo type field
    trigger(QUOTE_FORM_FIELDS.CARGO_TYPE);
  };

  const handleDangerousGoodsChange = (val: string) => {
    setValue(QUOTE_FORM_FIELDS.SPECIAL_HANDLING, val);
    // Surprised this is needed but it doesn't trigger the validation
    // when the errors are showing after a submit otherwise
    trigger(QUOTE_FORM_FIELDS.SPECIAL_HANDLING);
  };

  const handlePickupDateChange = (val: string) => {
    setValue(QUOTE_FORM_FIELDS.READY_FOR_PICKUP, val);
    trigger(QUOTE_FORM_FIELDS.READY_FOR_PICKUP);
  };

  const handleOriginSelect = (val?: PlaceInterface) => {
    if (!val) return;
    setValue(QUOTE_FORM_FIELDS.ORIGIN, val.name);
    setOriginCountryCode(val.countryCode || '');
    trigger(QUOTE_FORM_FIELDS.ORIGIN);
  };

  const handleDestinationSelect = (val?: PlaceInterface) => {
    if (!val) return;
    setValue(QUOTE_FORM_FIELDS.DESTINATION, val.name);
    setDestinationCountryCode(val.countryCode || '');
    trigger(QUOTE_FORM_FIELDS.DESTINATION);
  };

  const { form, fieldset, legend, section, formRowHalves, formRowThirds, formItemFullWidth, cargoDetails } = styles();

  const renderContent = () => {
    if (!errorMessage && isSubmitSuccessful) {
      return (
        <Block id="quote-form" className="bg-grey-50">
          <Heading variant="h2" as="h2" className="mb:xl lg:mb-xxl">
            Success. A Fluent Cargo agent will be in touch soon.
          </Heading>
          <ButtonGroup
            buttons={[
              {
                text: 'Request another Quote',
                onClick: () => router.reload(),
                variant: 'primaryDark',
              },
              {
                text: 'Go to the homepage',
                href: '/',
              },
              {
                text: 'Contact us',
                href: '/contact',
              },
            ]}
          />
        </Block>
      );
    }

    return (
      <Block
        id="quote-form"
        contentColumns={{
          left: 'w-full',
          center: 'w-full',
          right: 'w-full',
        }}
        className="bg-grey-50"
      >
        {errorMessage && (
          <UserMessage
            heading="Hmm, something went wrong"
            body={errorMessage}
            variant="error"
            className="!mt-0 mb-8 max-w-5xl"
          />
        )}
        <form className={form()} onSubmit={handleSubmit(handleQuoteRequest)}>
          <section className={section()}>
            <fieldset className={fieldset()}>
              <legend className={legend()}>
                <Heading variant="h1" as="h2" className="mb-sm">
                  Cargo Details
                </Heading>
              </legend>
              <div>
                <ol>
                  <li>
                    <div className="mb-4">
                      <Label
                        htmlFor={QUOTE_FORM_FIELDS.CARGO_TYPE}
                        className="flex items-center text-baseSm font-normal normal-case"
                      >
                        Select the type of cargo you are looking to ship
                      </Label>
                    </div>
                    <RadioGroup
                      errors={errors}
                      name={QUOTE_FORM_FIELDS.CARGO_TYPE}
                      mb={{ _: 'md', lg: 'xl' }}
                      variant="thirds"
                    >
                      <Controller
                        control={control}
                        name={QUOTE_FORM_FIELDS.CARGO_TYPE}
                        rules={{ required: true }}
                        render={({ field: { value, name } }) => (
                          <Radio
                            checked={value === CARGO_TYPES.FCL}
                            onChange={handleCargoTypeChange}
                            name={name}
                            value={CARGO_TYPES.FCL}
                            variant="chunky-button"
                          >
                            <RadioButtonInner heading="FCL" body="20' or 40' full shipping container" icon="fcl" />
                          </Radio>
                        )}
                      />
                      <Controller
                        control={control}
                        name={QUOTE_FORM_FIELDS.CARGO_TYPE}
                        rules={{ required: true }}
                        render={({ field: { value, name } }) => (
                          <Radio
                            checked={value === CARGO_TYPES.LCL}
                            onChange={handleCargoTypeChange}
                            name={name}
                            value={CARGO_TYPES.LCL}
                            variant="chunky-button"
                          >
                            <RadioButtonInner heading="LCL" body="Less than container load" icon="boxes" />
                          </Radio>
                        )}
                      />
                      <Controller
                        control={control}
                        name={QUOTE_FORM_FIELDS.CARGO_TYPE}
                        rules={{ required: true }}
                        render={({ field: { value, name } }) => (
                          <Radio
                            checked={value === CARGO_TYPES.AIR}
                            onChange={handleCargoTypeChange}
                            name={name}
                            value={CARGO_TYPES.AIR}
                            variant="chunky-button"
                          >
                            <RadioButtonInner heading="Air cargo" body="Pallets, boxes etc" icon="air-freight" />
                          </Radio>
                        )}
                      />
                      <Controller
                        control={control}
                        name={QUOTE_FORM_FIELDS.CARGO_TYPE}
                        rules={{ required: true }}
                        render={({ field: { value, name } }) => (
                          <Radio
                            checked={value === CARGO_TYPES.PERSONAL}
                            onChange={handleCargoTypeChange}
                            name={name}
                            value={CARGO_TYPES.PERSONAL}
                            variant="chunky-button"
                          >
                            <RadioButtonInner heading="Personal items" body="Bags, furniture etc" icon="boxset" />
                          </Radio>
                        )}
                      />
                      <Controller
                        control={control}
                        name={QUOTE_FORM_FIELDS.CARGO_TYPE}
                        rules={{ required: true }}
                        render={({ field: { value, name } }) => (
                          <Radio
                            checked={value === CARGO_TYPES.OTHER}
                            onChange={handleCargoTypeChange}
                            name={name}
                            value={CARGO_TYPES.OTHER}
                            variant="chunky-button"
                          >
                            <RadioButtonInner heading="Other" body="Not sure? Select this option" icon="boxset" />
                          </Radio>
                        )}
                      />
                    </RadioGroup>
                  </li>
                </ol>
                <fieldset
                  className={cargoDetails({ className: cargoTypeFieldValue === CARGO_TYPES.FCL ? 'block' : 'hidden' })}
                >
                  <ol className={formRowThirds()}>
                    <li>
                      <Controller
                        control={control}
                        name={QUOTE_FORM_FIELDS.QUANTITY}
                        render={({ field: { value, name, onChange } }) => {
                          return (
                            <NumberSpinner
                              label="Quantity"
                              name={name}
                              id={name}
                              value={value}
                              size="medium"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e)}
                              errors={errors}
                            />
                          );
                        }}
                      />
                    </li>
                    <li>
                      <Controller
                        control={control}
                        name={QUOTE_FORM_FIELDS.CONTAINER_SIZE}
                        render={({ field: { value, name, onBlur, onChange } }) => (
                          <Select
                            id={name}
                            label="Container size"
                            errors={errors}
                            options={[
                              {
                                title: '',
                                items: [
                                  { label: '20 foot', value: '20 foot' },
                                  { label: '40 foot', value: '40 foot' },
                                  { label: '53 foot', value: '53 foot' },
                                ],
                              },
                            ]}
                            selected={value}
                            onSelect={(val) => onChange(val?.value || '')}
                            onBlur={onBlur}
                            placeholder="Choose an option"
                            className="w-full"
                          />
                        )}
                      />
                    </li>
                  </ol>
                </fieldset>
                <fieldset
                  className={cargoDetails({ className: cargoTypeFieldValue === CARGO_TYPES.LCL ? 'block' : 'hidden' })}
                >
                  <ol className={formRowThirds()}>
                    <li>
                      <InputGroup
                        names={[QUOTE_FORM_FIELDS.TOTAL_VOLUME, QUOTE_FORM_FIELDS.UNIT_VOLUME]}
                        errors={errors}
                      >
                        <Controller
                          control={control}
                          name={QUOTE_FORM_FIELDS.TOTAL_VOLUME}
                          render={({ field }) => {
                            return <TextInput {...field} label="Total volume" errors={errors} hideErrorMessage />;
                          }}
                        />
                        <Controller
                          control={control}
                          name={QUOTE_FORM_FIELDS.UNIT_VOLUME}
                          render={({ field: { value, name, onBlur, onChange } }) => (
                            <Select
                              id={name}
                              errors={errors}
                              hideErrorMessage
                              options={[
                                {
                                  title: '',
                                  items: [
                                    { value: UNIT_VOLUME_CBM_FULL, label: UNIT_VOLUME_CBM_FULL },
                                    { value: UNIT_VOLUME_CFT_FULL, label: UNIT_VOLUME_CFT_FULL },
                                  ],
                                },
                              ]}
                              selected={value}
                              onSelect={(val) => onChange(val?.value || '')}
                              onBlur={onBlur}
                              placeholder="Choose"
                              className="w-64"
                            />
                          )}
                        />
                      </InputGroup>
                    </li>
                  </ol>
                </fieldset>
                <fieldset
                  className={cargoDetails({ className: cargoTypeFieldValue === CARGO_TYPES.AIR ? 'block' : 'hidden' })}
                >
                  <ol className={formRowThirds()}>
                    <li>
                      <InputGroup
                        names={[QUOTE_FORM_FIELDS.TOTAL_WEIGHT, QUOTE_FORM_FIELDS.UNIT_WEIGHT]}
                        errors={errors}
                      >
                        <Controller
                          control={control}
                          name={QUOTE_FORM_FIELDS.TOTAL_WEIGHT}
                          render={({ field }) => {
                            return (
                              <TextInput
                                {...field}
                                label="Total weight"
                                placeholder="0.00"
                                errors={errors}
                                hideErrorMessage
                              />
                            );
                          }}
                        />
                        <Controller
                          control={control}
                          name={QUOTE_FORM_FIELDS.UNIT_WEIGHT}
                          render={({ field: { value, name, onBlur, onChange } }) => (
                            <Select
                              id={name}
                              errors={errors}
                              hideErrorMessage
                              options={[
                                {
                                  title: '',
                                  items: [
                                    { value: UNIT_WEIGHT_KG, label: UNIT_WEIGHT_KG },
                                    { value: UNIT_WEIGHT_LB, label: UNIT_WEIGHT_LB },
                                  ],
                                },
                              ]}
                              selected={value}
                              onSelect={(val) => onChange(val?.value || '')}
                              onBlur={onBlur}
                              placeholder="Choose"
                              className="w-32"
                            />
                          )}
                        />
                      </InputGroup>
                    </li>
                  </ol>
                </fieldset>
                <fieldset
                  className={cargoDetails({
                    className: cargoTypeFieldValue === CARGO_TYPES.PERSONAL ? 'block' : 'hidden',
                  })}
                >
                  <ol className={formRowThirds()}>
                    <li>
                      <Controller
                        control={control}
                        name={QUOTE_FORM_FIELDS.NUMBER_OF_ITEMS}
                        render={({ field: { value, name, onChange } }) => {
                          return (
                            <NumberSpinner
                              label="Number of items"
                              name={name}
                              id={name}
                              value={value}
                              size="medium"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e)}
                              errors={errors}
                            />
                          );
                        }}
                      />
                    </li>
                    <li>
                      <InputGroup
                        names={[QUOTE_FORM_FIELDS.TOTAL_WEIGHT, QUOTE_FORM_FIELDS.UNIT_WEIGHT]}
                        errors={errors}
                      >
                        <Controller
                          control={control}
                          name={QUOTE_FORM_FIELDS.TOTAL_WEIGHT}
                          render={({ field }) => {
                            return (
                              <TextInput
                                {...field}
                                label="Total weight"
                                placeholder="0.00"
                                errors={errors}
                                hideErrorMessage
                              />
                            );
                          }}
                        />
                        <Controller
                          control={control}
                          name={QUOTE_FORM_FIELDS.UNIT_WEIGHT}
                          render={({ field: { value, name, onBlur, onChange } }) => (
                            <Select
                              id={name}
                              errors={errors}
                              options={[
                                {
                                  title: '',
                                  items: [
                                    { value: UNIT_WEIGHT_KG, label: UNIT_WEIGHT_KG },
                                    { value: UNIT_WEIGHT_LB, label: UNIT_WEIGHT_LB },
                                  ],
                                },
                              ]}
                              selected={value}
                              onSelect={(val) => onChange(val?.value || '')}
                              onBlur={onBlur}
                              placeholder="Choose"
                              hideErrorMessage
                              className="w-32"
                            />
                          )}
                        />
                      </InputGroup>
                    </li>
                  </ol>
                </fieldset>
                <fieldset
                  className={cargoDetails({
                    className: cargoTypeFieldValue === CARGO_TYPES.OTHER ? 'block' : 'hidden',
                  })}
                >
                  <ol className={formRowThirds()}>
                    <li>
                      <Controller
                        control={control}
                        name={QUOTE_FORM_FIELDS.ITEM_CATEGORY}
                        render={({ field: { value, name, onBlur, onChange } }) => {
                          return (
                            <Select
                              id={name}
                              label="Item category"
                              errors={errors}
                              options={[
                                {
                                  title: '',
                                  items: CONTAINER_ITEM_OPTIONS,
                                },
                              ]}
                              selected={value}
                              onSelect={(val) => onChange(val?.value || '')}
                              onBlur={onBlur}
                              placeholder="Choose an item category"
                            />
                          );
                        }}
                      />
                    </li>
                  </ol>
                </fieldset>
                <div
                  className={formItemFullWidth({
                    className: cn('mb-6 lg:mb-8', cargoTypeFieldValue ? 'block' : 'hidden'),
                  })}
                >
                  <Controller
                    control={control}
                    name={QUOTE_FORM_FIELDS.CARGO_DESCRIPTION}
                    render={({ field }) => {
                      return (
                        <TextInput
                          {...field}
                          label="Cargo description"
                          placeholder="Provide a basic description of your cargo"
                          errors={errors}
                          isTextArea
                          rows="6"
                          isOptional
                        />
                      );
                    }}
                  />
                </div>

                <Label htmlFor={QUOTE_FORM_FIELDS.SPECIAL_HANDLING}>Special handling</Label>
                <RadioGroup errors={errors} name={QUOTE_FORM_FIELDS.SPECIAL_HANDLING} variant="halves">
                  <Controller
                    control={control}
                    name={QUOTE_FORM_FIELDS.SPECIAL_HANDLING}
                    rules={{ required: true }}
                    render={({ field: { value, name } }) => (
                      <Radio
                        checked={value === SPECIAL_HANDLING.NO}
                        onChange={handleDangerousGoodsChange}
                        name={name}
                        value={SPECIAL_HANDLING.NO}
                        variant="chunky-button"
                      >
                        <RadioButtonInner
                          heading="General cargo"
                          body="No special handling required"
                          icon="box-approved"
                          variant="inline"
                        />
                      </Radio>
                    )}
                  />
                  <Controller
                    control={control}
                    name={QUOTE_FORM_FIELDS.SPECIAL_HANDLING}
                    rules={{ required: true }}
                    render={({ field: { value, name } }) => (
                      <Radio
                        checked={value === SPECIAL_HANDLING.YES}
                        onChange={handleDangerousGoodsChange}
                        name={name}
                        value={SPECIAL_HANDLING.YES}
                        variant="chunky-button"
                      >
                        <RadioButtonInner
                          heading="Special handling"
                          body="e.g. Dangerous goods, fragile, oversized, heavy or valuable items, live animals, etc"
                          icon="box-warning"
                          variant="inline"
                        />
                      </Radio>
                    )}
                  />
                </RadioGroup>
              </div>
            </fieldset>
          </section>
          <section className={section()}>
            <fieldset className={fieldset()}>
              <legend className={legend()}>
                <Heading variant="h1" as="h2" className="mb-sm">
                  Shipment Details
                </Heading>
              </legend>
              <div>
                <ol className={formRowHalves()}>
                  <li>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.ORIGIN}
                      render={({ field: { name } }) => {
                        return (
                          <LocationAutocomplete
                            name={name}
                            label="Loading / Pick up Location"
                            placeholder="Address, location or code (LOCODE/IATA)"
                            onSelect={handleOriginSelect}
                            errors={errors}
                          />
                        );
                      }}
                    />
                  </li>
                  <li>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.DESTINATION}
                      render={({ field: { name } }) => {
                        return (
                          <LocationAutocomplete
                            name={name}
                            label="Offload / Drop off Location"
                            placeholder="Address, location or code (LOCODE/IATA)"
                            onSelect={handleDestinationSelect}
                            errors={errors}
                          />
                        );
                      }}
                    />
                  </li>
                  <li>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.READY_FOR_PICKUP}
                      render={({ field: { value, name } }) => {
                        return (
                          <RadioTabs
                            name={name}
                            label="When do you need your shipment picked up?"
                            options={[
                              { value: 'Ready now', label: 'Ready now' },
                              { value: 'Next week', label: 'Next week' },
                              { value: 'In two weeks', label: 'In two weeks' },
                              { value: 'TBC', label: 'TBC' },
                            ]}
                            onChange={handlePickupDateChange}
                            selected={value}
                            errors={errors}
                          />
                        );
                      }}
                    />
                  </li>
                  <div className={formItemFullWidth()}>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.SHIPMENT_DETAILS}
                      render={({ field }) => {
                        return (
                          <TextInput
                            {...field}
                            label="Other information about the shipment?"
                            placeholder="Provide any additional details that we might need to know"
                            errors={errors}
                            isTextArea
                            rows="6"
                            isOptional
                          />
                        );
                      }}
                    />
                  </div>
                </ol>
              </div>
            </fieldset>
          </section>
          <section className={section()}>
            <fieldset className={fieldset()}>
              <legend className={legend()}>
                <Heading variant="h1" as="h2" className="mb-sm">
                  Contact Details
                </Heading>
              </legend>
              <div>
                <ol className={formRowHalves()}>
                  <li>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.NAME}
                      render={({ field }) => {
                        return (
                          <TextInput
                            {...field}
                            label="Contact name"
                            placeholder="Enter a first and last name"
                            errors={errors}
                            disabled={!!session?.user}
                          />
                        );
                      }}
                    />
                  </li>
                  <li>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.BUSINESS_NAME}
                      render={({ field }) => {
                        return (
                          <TextInput {...field} label="Business name (If applicable)" errors={errors} isOptional />
                        );
                      }}
                    />
                  </li>
                  <li>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.EMAIL}
                      render={({ field }) => {
                        return (
                          <TextInput {...field} label="Email address" errors={errors} disabled={!!session?.user} />
                        );
                      }}
                    />
                  </li>
                  <li>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.PHONE}
                      render={({ field }) => {
                        return <TextInput {...field} label="Phone number" errors={errors} isOptional />;
                      }}
                    />
                  </li>
                  <li>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.COUNTRY}
                      render={({ field: { value, name, onBlur, onChange } }) => (
                        <Select
                          id={name}
                          label="Country"
                          errors={errors}
                          options={[
                            {
                              title: '',
                              items: countriesSelectBoxOptions,
                            },
                          ]}
                          selected={value}
                          onSelect={(val) => onChange(val?.value || '')}
                          onBlur={onBlur}
                          placeholder="Where are you located?"
                          disabled={!!session?.user?.country}
                          className="w-full"
                        />
                      )}
                    />
                  </li>
                  <li>
                    <Controller
                      control={control}
                      name={QUOTE_FORM_FIELDS.SENDS_CARGO_REGULARLY}
                      render={({ field: { value, name, onBlur, onChange } }) => (
                        <Select
                          id={name}
                          label="Do you send cargo regularly?"
                          errors={errors}
                          tooltipContent={
                            <Text variant="sm" className="mb-0 p-1">
                              This helps us pair you with the appropriate Freight Forwarder based on your shipping
                              experience.
                            </Text>
                          }
                          options={[
                            {
                              title: '',
                              items: [
                                {
                                  label: 'Yes, all the time',
                                  value: 'Yes, all the time',
                                },
                                {
                                  label: 'Semi-regularly',
                                  value: 'Semi-regularly',
                                },
                                {
                                  label: 'No, not very often',
                                  value: 'No, not very often',
                                },
                                {
                                  label: 'This is my first time',
                                  value: 'This is my first time',
                                },
                              ],
                            },
                          ]}
                          selected={value}
                          onSelect={(val) => onChange(val?.value || '')}
                          onBlur={onBlur}
                          placeholder="Select an option"
                          isOptional
                          className="w-full"
                        />
                      )}
                    />
                  </li>
                </ol>
              </div>
            </fieldset>
          </section>
          <section className={section({ className: 'lg:mt-4' })}>
            <fieldset className={fieldset()}>
              <div />
              <div>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                  onChange={onReCaptchaChange}
                />
                <Button
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  className="mb-base mt-base"
                  iconRight="arrow-right"
                  errors={errors}
                  iconAnimX
                >
                  Submit Quote Request
                </Button>

                <Text variant="secondary" className="mt-4 text-text-secondary">
                  By submitting this request you agree to the conditions outlined in our{' '}
                  <Link href={marketingPages.terms.url}>{marketingPages.terms.label}</Link> and{' '}
                  <Link href={marketingPages.privacy.url}>{marketingPages.privacy.label}</Link>. By submitting this
                  request you also understand and agree that your details may be shared with third-party partners in
                  order to fulfill your request.
                </Text>
              </div>
            </fieldset>
          </section>
        </form>
      </Block>
    );
  };

  return renderContent();
};

const styles = tv({
  slots: {
    form: '[&_ol>li]:mb-5',
    fieldset: 'contents',
    legend: 'mb-4 block lg:mb-0 [&_p]:mb-0',
    formRowHalves: 'grid gap-x-4 lg:grid-cols-2',
    formRowThirds: 'grid gap-x-4 lg:grid-cols-3',
    formItemFullWidth: 'col-span-full',
    cargoDetails: 'm-0 border-0 p-0',
    section:
      'mb-8 grid gap-4 border-b border-solid border-b-grey-200 pb-8 lg:mb-16 lg:grid-cols-[2fr_4fr] lg:gap-8 lg:pb-16 [&:nth-last-child(-n+2)]:mb-0 [&:nth-last-child(-n+2)]:border-b-0 [&:nth-last-child(-n+2)]:pb-0',
  },
});

export { QuoteForm };
